import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  selectAllMaterials,
  selectAllSections,
  useMaterials,
} from "../features/materialsSections/materialSectionSlice";
import { selectToken } from "../features/user/userSlice";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { ProjectSlice, selectDesignStatus, selectProject, useProject } from "../features/projects/projectSlice";
import { report_exception } from "./ui-components/SXAlert/SXAlertMessage";
import { SXDesignReviewPanel } from "./SXDesignReviewPanel";
import { selectDesignReviewOpen } from "../features/designReview/designReviewSlice";
import { ConcreteDesignSettings } from "./SXMemberDesignConcrete";
import { SteelTimberDesignSettings } from "./SXMemberDesignSteelTimber";
import { getMemberMaterialGroup, withAutoDesign, type BuildAutoDesign } from "../helpers/memberDesign";

interface MemberDesignPanelProps {
  selLC?: string;
  /**
   * Called when a design result is approved, i.e. committed to the project, just before the
   * analysis and the code check are re-run over it. Lets the host switch to the code check, which
   * is where that run's governing controls appear. Declining keeps the user here to adjust the
   * settings and run again, so it does not fire.
   */
  onDesignApproved?: () => void;
}

/**
 * Member (auto) design pane. Owns everything the three materials share — the busy lock, starting a
 * run, and handing a finished run to the review panel — and delegates the settings themselves to
 * the panel for the column's material, since concrete, steel and timber are designed by different
 * services with very different inputs.
 */
export const MemberDesignPanel = ({ selLC: _selLC, onDesignApproved }: MemberDesignPanelProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { designAsync } = useProject();
  const { getMaterials, getSections } = useMaterials();
  const allSections = useAppSelector(selectAllSections);
  const allMaterials = useAppSelector(selectAllMaterials);
  const token = useAppSelector(selectToken);
  const selProject = useAppSelector(selectProject);
  const designStatus = useAppSelector(selectDesignStatus);
  // A completed design run is staged for review — the pane shows the result instead of the design
  // settings until the user declines or approves (both clear the staged result).
  const isReviewingDesign = useAppSelector(selectDesignReviewOpen);
  // Locks the panel while a design run is in flight. Covers both the local prep work
  // (fetching missing material/section data before the thunk dispatches) and the
  // designProject thunk itself, which only flips designStatus to "loading" once it runs.
  const [isPreparingDesign, setIsPreparingDesign] = useState(false);
  const isDesigning = isPreparingDesign || designStatus === "loading";

  const materialGroup = getMemberMaterialGroup(selProject?.data);
  // Before the project has loaded there is no material to read, which is not the same as a material
  // nothing can design — keep the pane empty until there is something true to say about it.
  const hasProjectInput = !!selProject?.data?.data?.input?.structures?.[0]?.elements?.[0];

  useEffect(() => {
    if (token && (!allSections || Object.keys(allSections).length === 0)) {
      getSections();
    }
    if (token && (!allMaterials || Object.keys(allMaterials).length === 0)) {
      getMaterials();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Start a design run from the payload the material's settings assembled: cache it on the project
  // (so a reopened project comes back with the same settings) and post it to /design. The result is
  // staged for review by the designProject thunk, not applied here.
  const runDesign = async (build: BuildAutoDesign) => {
    if (isDesigning) return;
    try {
      setIsPreparingDesign(true);
      const autoDesignJson = await build();
      if (!autoDesignJson) return;

      dispatch(ProjectSlice.actions.setAutoDesign(autoDesignJson));

      if (selProject) {
        await designAsync(withAutoDesign(selProject, autoDesignJson));
      }
    } catch (x) {
      report_exception(t, x);
    } finally {
      setIsPreparingDesign(false);
    }
  };

  if (isReviewingDesign) {
    return (
      <Container fluid id="sx-table-container" className="sx-code-check-design">
        <SXDesignReviewPanel onApproved={onDesignApproved} />
      </Container>
    );
  }

  return (
    <Container
      fluid
      id="sx-table-container"
      className={`sx-code-check-design ${isDesigning ? "is-locked" : ""}`}
      aria-busy={isDesigning}
    >
      {/* The panel's own busy overlay was removed: the app-wide SXCalcProgressOverlay (App.tsx)
          now covers the whole window — including this panel — for every long calc route, design
          included, so a second overlay here was redundant. The settings themselves still disable
          via `isDesigning` below, which also covers the local prep step before designStatus flips
          to "loading". */}
      <Row className="align-items-center">
        <Col>
          {materialGroup === "Concrete" && <ConcreteDesignSettings isDesigning={isDesigning} runDesign={runDesign} />}
          {(materialGroup === "Steel" || materialGroup === "Timber") && (
            <SteelTimberDesignSettings group={materialGroup} isDesigning={isDesigning} runDesign={runDesign} />
          )}
          {materialGroup === null && hasProjectInput && (
            <div className="sx-cd-no-match">
              {t("design_material_unsupported_.label", "Member design is not available for this material.")}
            </div>
          )}
        </Col>
      </Row>
    </Container>
  );
};
